<template>
    <div>
        <div class="mb-3 text-right">
            <hts-form-button
                v-if="checkPermission(layoutPermissions.connect_add)"
                color="main"
                icon-name="hi-plus-circle-icon"
                icon-position="right"
                type="submit"
                @click="handleOpenConnect">
                {{ $t('labels.add') }}
            </hts-form-button>
        </div>

        <hts-data-table
            ref="dataTables"
            :headers="headers"
            :server="apiListOrder"
            store-name="orderDataTable"
            store-key="Order" />

        <CreateConnect
            ref="createConnect"
            @refreshTables="refreshTables" />

        <hts-modal
            v-if="checkPermission(layoutPermissions.order_delete)"
            :show="showModalRespond"
            title="Phản hồi kết nối"
            @close="handleCloseModalRespond">
            <div class="max-w-xl mx-auto p-6">
                <p class="text-lg text-gray-700">
                    Bạn nhận được lời mời kết nối từ workspace nhà xe:
                    <span class="font-bold text-blue-800">
                        {{ integrationSelected.senderWsId }}
                    </span>
                </p>

                <h3 class="mt-6 text-xl font-semibold">
                    Sau khi xác nhận lời mời:
                </h3>
                <ul class="list-disc pl-5 mt-2 text-gray-700">
                    <li class="mb-2">
                        Hệ thống sẽ dựa vào thông tin công ty của bạn để tự động tạo một khách hàng mới vào workspace nhà xe
                        <span class="font-bold text-blue-800">
                            {{ integrationSelected.senderWsId }}
                        </span>
                    </li>
                    <li>
                        Bạn sẽ được cấp quyền truy cập để xem thông tin đơn hàng và chuyến hàng của bạn.
                    </li>
                </ul>
            </div>

            <div class="flex justify-end mt-5 gap-5">
                <hts-form-button
                    color="primary"
                    type="button"
                    :outline="false"
                    @click="handleRespond('accept')">
                    {{ $t('labels.confirm') }}
                </hts-form-button>

                <hts-form-button
                    type="button"
                    :outline="false"
                    @click="handleRespond('reject')">
                    {{ $t('labels.reject') }}
                </hts-form-button>
            </div>
        </hts-modal>
    </div>
</template>
<script setup lang="ts">
import CreateConnect from './ignore/add.vue'
import Status from '~/components/status.vue'
import layoutPermissions from '~/data/permissions/layout-permissions'
import Actions from '~/pages/trucker-[id]/driver/ignore/actions.vue'
const apiListOrder = $url.company.integration.internal.gets
const { $formatters } = useNuxtApp()
const createConnect = ref()
const dataTables = ref()
const showModalRespond = ref<boolean>(false)
const integrationSelected = ref<{ [key: string]: any }>({})

const respond = (data: any) => {
  integrationSelected.value = data
  showModalRespond.value = true
}

const checkShowRespond = (data: any) => {
  return data.isWaitingForYourApproval
}

const handleCloseModalRespond = () => {
  integrationSelected.value = {}
  showModalRespond.value = false
}

const handleRespond = (type: string) => {
  const respondUrl = $url.company.integration.internal.respond

  $api(respondUrl, { body: { type, id: integrationSelected.value._id } }).then((data) => {
    const resData: any = data?.data?.value

    if (resData?.success) {
      if (type === 'accept') {
        process.client && $toast().success('Kết nối thành công')
      }
      if (type === 'reject') {
        process.client && $toast().success('Từ chối kết nối thành công')
      }

      refreshTables()
      handleCloseModalRespond()
      resetIntegrationSelected()
    }
  })
}

const resetIntegrationSelected = () => {
  integrationSelected.value = {}
}

const headers = [
  {
    field: 'senderWsId',
    headerName: 'Workspace gửi kết nối',
    sortable: true,
    filter: 'agTextColumnFilter',
    valueGetter: (params: any) => {
      return params.data.senderWsId || ''
    }
  },
  {
    field: 'receiverWsId',
    headerName: 'Workspace nhận kết nối',
    sortable: true,
    filter: 'agTextColumnFilter',
    valueGetter: (params: any) => {
      return params.data.receiverWsId || ''
    }
  },
  {
    field: 'status',
    headerName: $t('labels.status'),
    cellRenderer: Status,
    cellRendererParams: {
      module: 'integrationInternalStatus',
      label: '',
      keyStatus: 'status'
    },
    filter: 'TableFilterStatus',
    filterItems: [
      { name: $t('labels.all'), value: null },
      {
        name: $t('labels.pendingApproval'),
        value: 'pending',
        class: { '!bg-yellow-100': true, '!text-yellow-800': true }
      },
      { name: $t('labels.approved'), value: 'approved', class: { '!bg-green-100': true, '!text-green-800': true } },
      { name: $t('labels.reject'), value: 'reject', class: { '!bg-red-100': true, '!text-red-800': true } }
    ],
    filterModelDefault: { type: 'equals', filterType: 'text' }
  },
  {
    field: 'createdAt',
    headerName: $t('labels.createdAt'),
    sortable: true,
    filter: 'agDateColumnFilter',
    valueGetter: (params: any) => {
      return $formatters.datetime(params.data.createdAt)
    }
  },
  {
    field: 'action',
    pinned: 'right',
    sortable: false,
    lockPosition: true,
    lockPinned: true,
    headerName: $t('labels.action'),
    cellStyle: { overflow: 'visible' },
    cellRenderer: Actions,
    cellRendererParams: {
      storeKey: 'orderDataTable',
      storeName: 'Order',
      items: [
        {
          icon: 'hi-eye-icon',
          label: 'Phản hồi',
          class: 'text-blue-600 hover:text-blue-500',
          permissions: checkPermission(layoutPermissions.connect_add),
          click: respond,
          validate: checkShowRespond
        }
      ]
    }
  }
]

const refreshTables = () => {
  if (dataTables.value) {
    dataTables.value.refresh()
  }
}

const handleOpenConnect = () => {
  createConnect.value && createConnect.value.open()
}
</script>
